import axios from "axios";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import { Input } from "src/components/ui/input";
import { useLanguage } from "src/context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "src/lib/api";
import { SubLessonData } from "../../../../../lib/languages";

const DialogDeleteQuiz = ({ item, fetchQuizzes }) => {
  const { language } = useLanguage();
  const t = SubLessonData[language] || SubLessonData["en"];

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const confirmationWord = "DELETE";
  const isConfirmEnabled = confirmText.toUpperCase() === confirmationWord;

  const handleDeleteQuiz = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.delete(`${BaseUrlApi}/quizzes/${item.bid}`);
      toast("Quiz Deleted", {
        description: "The quiz has been successfully removed 🗑️",
      });
      await fetchQuizzes();
      setOpen(false);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="text-red-500 h-7 w-7">
          <Trash2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-md"
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        <DialogHeader className="gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <DialogTitle className="text-red-500">
              {t.deleteQuiz} "
              {item.title[language] || item.title.en || item.title.ar || "---"}"
            </DialogTitle>
          </div>
          <DialogDescription>{t.deleteHomeWorkConfirm}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label htmlFor="confirm" className="text-sm font-medium">
            {t.typeToConfirmDelete}
          </label>
          <Input
            id="confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className={
              !isConfirmEnabled
                ? "border-destructive focus-visible:ring-0"
                : "border-green-500 focus-visible:ring-0"
            }
            placeholder={confirmationWord}
          />
        </div>
        <DialogFooter className="sm:justify-between gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t.cancel}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDeleteQuiz}
            disabled={loading || !isConfirmEnabled}
            className="gap-2"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            {t.deleteQuiz}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DialogDeleteQuiz;
